import { useRef } from "react";
import { Camera, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useBolUpload } from "@/hooks/useBolUpload";

interface BolUploadButtonProps {
  orderId: string;
  /** Human-readable order identifier (e.g. AOSC-DETM-00001). Used as the
   *  S3 `order_id` tag so NetSuite can route the BoL. */
  orderUniqueId: string;
  driverName?: string;
}

export function BolUploadButton({ orderId, orderUniqueId, driverName }: BolUploadButtonProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadFiles, isUploading } = useBolUpload({
    orderId,
    orderUniqueId,
    mode: "driver",
    changedBy: driverName,
  });

  // Check if BoL images already exist for this order
  const { data: existingBolImages, isLoading } = useQuery({
    queryKey: ["bol-images", orderId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("order_bol_images")
        .select("id, image_url, image_path")
        .eq("order_id", orderId);
      
      if (error) throw error;
      return data || [];
    },
  });

  const hasBolUploaded = existingBolImages && existingBolImages.length > 0;

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    try {
      await uploadFiles(files);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (isLoading) {
    return (
      <Button variant="outline" size="sm" disabled className="w-full">
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        Loading...
      </Button>
    );
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        multiple
        onChange={handleFileChange}
        className="hidden"
      />
      <Button
        variant={hasBolUploaded ? "secondary" : "default"}
        size="sm"
        onClick={handleButtonClick}
        disabled={isUploading}
        className="w-full"
      >
        {isUploading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Uploading...
          </>
        ) : hasBolUploaded ? (
          <>
            <Check className="h-4 w-4 mr-2" />
            BoL Uploaded ({existingBolImages.length}) — Add More
          </>
        ) : (
          <>
            <Camera className="h-4 w-4 mr-2" />
            Add Bill of Lading
          </>
        )}
      </Button>
    </>
  );
}